import { create } from 'zustand';
import { UserManager, WebStorageStateStore, type User } from 'oidc-client-ts';
import type { AuthUser } from '@/types';

// Zitadel OIDC config baked from build-time env vars.
//   VITE_ZITADEL_DOMAIN     — e.g. https://issuer.zitadel.cloud (used as `authority`)
//   VITE_ZITADEL_CLIENT_ID  — OIDC client id
//   VITE_ZITADEL_PROJECT_ID — used to request the project audience scope
const env = (import.meta as any).env ?? {};
const issuer    = String(env.VITE_ZITADEL_DOMAIN     ?? '').replace(/\/$/, '');
const clientId  = String(env.VITE_ZITADEL_CLIENT_ID  ?? '');
const projectId = String(env.VITE_ZITADEL_PROJECT_ID ?? '');

if (!issuer || !clientId) {
  console.warn('[auth] missing VITE_ZITADEL_DOMAIN or VITE_ZITADEL_CLIENT_ID — sign-in will fail');
}

export const oidcManager = new UserManager({
  authority:                issuer,
  client_id:                clientId,
  redirect_uri:             `${window.location.origin}/callback`,
  post_logout_redirect_uri: `${window.location.origin}/loggedout`,
  response_type:            'code',
  // The `urn:zitadel:iam:user:metadata` scope makes Zitadel project every
  // user-metadata key into the token as `urn:zitadel:iam:user:metadata:<key>`
  // (base64). We read company_slug from there — the Complement-Token action
  // that was supposed to emit a clean `company_slug` claim silently no-ops in
  // Zitadel v4.15.0, so this native projection is the reliable source.
  scope:                    `openid profile email offline_access urn:zitadel:iam:org:project:id:${projectId}:aud urn:zitadel:iam:user:metadata`,
  automaticSilentRenew:     true,
  // We deliberately do NOT auto-redirect to Zitadel from boot — the App
  // renders a sign-in screen instead. This prevents Zitadel SSO from silently
  // re-authenticating the operator's Zitadel-admin session into the dashboard.
  monitorSession:           false,
  // sessionStorage so closing all tabs ends the local session. The
  // Zitadel server-side session is ended via signoutRedirect on logout.
  userStore:                new WebStorageStateStore({ store: window.sessionStorage }),
});

// Zitadel projects user-metadata values as base64 — and the userinfo
// endpoint returns it URL-safe + UNPADDED (e.g. "Y2JzLWJvcmluZw" → "cbs-boring"),
// which raw atob() rejects. Normalise (url→std alphabet, re-pad) before decode.
function decodeMetadataClaim(v: unknown): string | undefined {
  if (typeof v !== 'string' || v === '') return undefined;
  try {
    let s = v.replace(/-/g, '+').replace(/_/g, '/');
    while (s.length % 4) s += '=';
    return atob(s);
  } catch { return v; }
}

// company_slug lives in Zitadel user metadata, which Zitadel exposes via the
// userinfo endpoint (NOT the JWT access token, and oidc-client-ts doesn't
// merge it into u.profile here). The Complement-Token action that was meant
// to flatten it into a claim no-ops in v4.15.0. So we fetch userinfo directly
// and read the nested `urn:zitadel:iam:user:metadata` object. Best-effort:
// a failure leaves companySlug null (→ "No company assigned" page), which is
// the correct degraded state for a genuinely company-less user.
async function fetchCompanySlug(accessToken: string): Promise<string | null> {
  if (!issuer || !accessToken) return null;
  try {
    const res = await fetch(`${issuer}/oidc/v1/userinfo`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!res.ok) return null;
    const j = await res.json();
    const md = j['urn:zitadel:iam:user:metadata'];
    const raw = md && typeof md === 'object' ? (md as Record<string, unknown>).company_slug : undefined;
    return decodeMetadataClaim(raw) ?? null;
  } catch {
    return null;
  }
}

// Fill companySlug from userinfo when the token didn't carry it. Admins
// legitimately have none (cross-company firehose), so skip the round-trip.
async function enrichCompanySlug(au: AuthUser, accessToken: string): Promise<AuthUser> {
  if (au.isAdmin || au.companySlug) return au;
  const slug = await fetchCompanySlug(accessToken);
  return slug ? { ...au, companySlug: slug } : au;
}

function extractAuthUser(u: User): AuthUser {
  let tokenPayload: Record<string, unknown> = {};
  try {
    const parts = u.access_token.split('.');
    if (parts.length >= 2) tokenPayload = JSON.parse(atob(parts[1]));
  } catch { /* ignore */ }

  const rawRoles = tokenPayload.roles ?? tokenPayload['urn:zitadel:iam:org:project:roles'];
  const roles: string[] = Array.isArray(rawRoles)
    ? (rawRoles as string[])
    : (rawRoles && typeof rawRoles === 'object' ? Object.keys(rawRoles as object) : []);

  const profile = u.profile as Record<string, unknown>;
  // company_slug source priority. Zitadel projects user metadata into the
  // ID TOKEN (u.profile) + userinfo, NOT the JWT access token — and the
  // Complement-Token action that was meant to emit a clean `company_slug`
  // claim no-ops in v4.15.0. So the reliable source is the base64 metadata
  // claim on the ID token (profile), enabled by the
  // `urn:zitadel:iam:user:metadata` scope + "user info inside ID token".
  const companySlug =
    (tokenPayload.company_slug as string | undefined) ??
    decodeMetadataClaim(profile['urn:zitadel:iam:user:metadata:company_slug']) ??
    decodeMetadataClaim(tokenPayload['urn:zitadel:iam:user:metadata:company_slug']) ??
    (profile.company_slug as string | undefined) ??
    null;

  return {
    token:       u.access_token,
    idToken:     u.id_token ?? '',
    name:        (profile.name as string) ?? (profile.preferred_username as string) ?? (profile.email as string) ?? '',
    email:       (profile.email as string) ?? '',
    sub:         (profile.sub as string)   ?? '',
    isAdmin:     roles.includes('laser_admin') || roles.includes('phos-admin') || roles.includes('phos_admin'),
    roles,
    companySlug,
  };
}

// Resolve the active company for a freshly-loaded user. Non-admins are
// locked to their own company_slug — we deliberately ignore any stale
// `lc-active-company` left in localStorage by a previous session (an
// admin's pick, or another tenant on a shared browser). Honoring it would
// send a mismatching `?company=` and the api 403s every scoped request
// ("company mismatch"), hard-locking the dashboard with no recourse since
// the CompanySwitcher is hidden for single-company users. Admins keep
// their saved pick.
function companyForUser(user: AuthUser | null): string | null {
  if (user && !user.isAdmin) return user.companySlug ?? null;
  return localStorage.getItem('lc-active-company');
}

interface AuthState {
  user:     AuthUser | null;
  ready:    boolean;
  company:  string | null;
  /** Per-user feature flag overrides loaded from /v1/users/me. Forces
   *  the listed keys ON during evaluateFlag(). Admin users get every
   *  flag regardless via the role rule, so this slice is only
   *  meaningful for company_user accounts. */
  flagOverrides: string[];
  init:     () => Promise<void>;
  login:    (force?: boolean) => Promise<void>;
  logout:   () => Promise<void>;
  setCompany: (slug: string | null) => void;
  handleCallback: () => Promise<void>;
  refreshFlagOverrides: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user:    null,
  ready:   false,
  company: localStorage.getItem('lc-active-company'),
  flagOverrides: [],

  init: async () => {
    if (get().ready) return;
    try {
      const u = await oidcManager.getUser();
      if (u && !u.expired) {
        const au = await enrichCompanySlug(extractAuthUser(u), u.access_token);
        set({ user: au, ready: true, company: companyForUser(au) });
        void get().refreshFlagOverrides();
      } else {
        set({ ready: true });
      }
    } catch (err) {
      console.warn('[auth] init failed', err);
      set({ ready: true });
    }
  },

  // Best-effort. A 404 or 502 here shouldn't block the dashboard
  // from booting — flag overrides just resolve to empty and the
  // user sees the default flag set for their role. Logged once so
  // a misconfigured api surface is visible.
  refreshFlagOverrides: async () => {
    const u = get().user;
    if (!u?.token) { set({ flagOverrides: [] }); return; }
    try {
      const env = (import.meta as any).env ?? {};
      const apiBase = String(env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');
      const url = apiBase ? `${apiBase}/v1/users/me` : '/api/v1/users/me';
      const res = await fetch(url, { headers: { Authorization: `Bearer ${u.token}` } });
      if (!res.ok) { set({ flagOverrides: [] }); return; }
      const me = await res.json();
      const flags = Array.isArray(me?.feature_flags) ? me.feature_flags as string[] : [];
      set({ flagOverrides: flags });
    } catch (err) {
      console.warn('[auth] flag override fetch failed', err);
      set({ flagOverrides: [] });
    }
  },

  login: async (force = false) => {
    await oidcManager.signinRedirect({
      extraQueryParams: force ? { prompt: 'login' } : undefined,
    });
  },

  logout: async () => {
    const u = await oidcManager.getUser().catch(() => null);
    const idTokenHint = u?.id_token;
    try {
      await oidcManager.signoutRedirect({
        id_token_hint:            idTokenHint,
        post_logout_redirect_uri: `${window.location.origin}/loggedout`,
      });
    } catch (err) {
      console.warn('[auth] signoutRedirect failed, falling back to local clear', err);
      await oidcManager.removeUser().catch(() => {});
      set({ user: null });
      window.location.replace('/loggedout');
    }
  },

  setCompany: (slug) => {
    if (slug) localStorage.setItem('lc-active-company', slug);
    else      localStorage.removeItem('lc-active-company');
    set({ company: slug });
  },

  handleCallback: async () => {
    const u = await oidcManager.signinRedirectCallback();
    const au = await enrichCompanySlug(extractAuthUser(u), u.access_token);
    set({ user: au, ready: true, company: companyForUser(au) });
    void get().refreshFlagOverrides();
  },
}));

// Wire global oidc events to keep the store in sync (silent renew etc).
oidcManager.events.addUserLoaded((u: User) => {
  void (async () => {
    const au = await enrichCompanySlug(extractAuthUser(u), u.access_token);
    useAuthStore.setState({ user: au, company: companyForUser(au) });
  })();
  void useAuthStore.getState().refreshFlagOverrides();
});
oidcManager.events.addUserUnloaded(() => {
  useAuthStore.setState({ user: null, flagOverrides: [] });
});
oidcManager.events.addSilentRenewError((err) => {
  console.warn('[auth] silent renew failed', err);
});

// Live-apply per-user feature-flag changes without a manual reload. Zitadel
// access tokens are long-lived, so silent renew alone lags badly. Re-pull
// /v1/users/me flag overrides whenever the tab regains focus / visibility —
// an admin toggling a user's flags then the user clicking back takes effect
// immediately. (Role-driven flags still need a fresh token; only the
// override list refreshes here.) refreshFlagOverrides is a no-op when signed
// out and updates the store in place, so the nav + gates re-render.
if (typeof window !== 'undefined') {
  const refresh = () => { void useAuthStore.getState().refreshFlagOverrides(); };
  window.addEventListener('focus', refresh);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') refresh();
  });
}
